<div #pdfArea class="min-h-screen bg-white font-sans text-slate-800">
	<header class="bg-resume-teal text-white px-8 py-10 md:py-12">
		<div class="max-w-6xl mx-auto">
			<h1 class="text-4xl md:text-6xl font-bold mb-2 tracking-tight">Burakhan Şamlı</h1>
			<p class="text-xl md:text-2xl font-light text-resume-teal-light mb-6">Forensic Informatics Engineer | AI, Computer Vision, NLP & MLOps
			</p>

			<p class="text-sm md:text-base leading-relaxed opacity-90 mb-8 max-w-4xl">
				&nbsp;&nbsp;AI Specialist with a background in Forensic Informatics Engineering, specializing in Computer Vision(CV), Natural Language Processing(NLP), and Remote Sensing-based AI systems.
				Experienced in developing end-to-end, scalable AI solutions including biometric identification, object detection and tracking, multi-camera video analytics, and satellite image analysis for real-world applications.
				Strong hands-on experience with Transformer-based architectures across vision and language domains, as well as deploying AI models in production using containerized and microservice-based architectures with Docker and Kubernetes.
			</p>

			<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm font-medium">
				<div class="flex items-center gap-2 hover:text-white/80 transition-colors">
					<lucide-icon [img]="MailIcon" class="my-icon"></lucide-icon>
					<a href="mailto:burakhansamli@proton.me">burakhansamli@proton.me</a>
				</div>
				<div class="flex items-center gap-2 hover:text-white/80 transition-colors">
					<lucide-icon [img]="PhoneIcon" class="my-icon"></lucide-icon>
					<span><a [href]="'tel:'+tel">{{ tel }}</a></span>
				</div>
				<div class="flex items-center gap-2 hover:text-white/80 transition-colors">
					<lucide-icon [img]="MapPinIcon" class="my-icon"></lucide-icon>
					<span>Ankara</span>
				</div>
				<div class="flex items-center gap-2 hover:text-white/80 transition-colors">
					<lucide-icon [img]="LinkedInIcon" class="my-icon"></lucide-icon>
					<a href="https://www.linkedin.com/in/burakhan-şamlı-0000/">Burakhan Şamlı</a>
				</div>
				<div class="flex items-center gap-2 hover:text-white/80 transition-colors">
					<lucide-icon [img]="GithubIcon" class="my-icon"></lucide-icon>
					<a href="https://github.com/blitzkrieg0000">github.com/blitzkrieg0000</a>
				</div>
				<div class="flex items-center gap-2 hover:text-white/80 transition-colors">
					<lucide-icon [img]="GlobeIcon" class="my-icon"></lucide-icon>
					<a href="https://blitzkrieg0000.github.io/">https://blitzkrieg0000.github.io/</a>
				</div>
			</div>
		</div>
	</header>

	<main class="max-w-6xl mx-auto px-8 py-12 grid grid-cols-1 md:grid-cols-12 gap-12">
		<!-- Column 0 -->
		<div class="md:col-span-7 lg:col-span-8 space-y-10">
			<!-- CV -->
			<section>
				<div>
					<h2 class="text-resume-gold text-2xl font-bold uppercase border-b-2 border-resume-gold/30 pb-2 mb-2 tracking-wide">Work Experience</h2>
					<div class="space-y-8 relative pl-4 border-l-2 border-slate-300 ml-2">
						<div class="relative mb-2">
							<div class="absolute -left-[25px] top-1 w-4 h-4 rounded-full bg-resume-gold border-4 border-white shadow-sm"></div>
							<div class="flex justify-between items-baseline mb-1">
								<h3 class="text-xl font-bold text-slate-900">Hexaops A.Ş.</h3>
								<span class="text-sm italic text-slate-500">Teknokent, Kahramanmaraş</span>
								<span class="text-xs font-semibold text-resume-teal-dark bg-resume-teal/10 px-2 py-0.5 rounded">10/2024 - 05/2025</span>
							</div>
						</div>

						<div class="space-y-8 relative pl-4 border-l-2 border-slate-200 ml-0">
							<div class="relative">
								<div class="absolute -left-[23px] top-2 w-3 h-3 rounded-full bg-resume-teal border-3 border-white shadow-sm"></div>
								<div class="flex justify-between items-baseline mb-3">
									<h4 class="text-lg font-semibold text-slate-700">AI Engineer & Software Developer</h4>
								</div>
								<ul class="list-disc list-outside ml-4 space-y-2 text-sm text-slate-600 marker:text-resume-teal">
									<li>Contributed to LLM fine-tuning workflows with an emphasis on machine translation models.</li>
									<li>Provided engineering support for TTS, STT, and voice cloning pipelines in production environments.</li>
									<li>Applied Fully Sharded Data Parallel (FSDP) techniques to enable LLM fine-tuning on limited GPU resources.</li>
									<li>Improved training efficiency by reducing memory footprint, training time, and overall compute costs.</li>
								</ul>
							</div>
						</div>

						<div class="relative mb-2">
							<div class="absolute -left-[25px] top-1 w-4 h-4 rounded-full bg-resume-gold border-4 border-white shadow-sm"></div>
							<div class="flex justify-between items-baseline mb-1">
								<h3 class="text-xl font-bold text-slate-900">DGH Arge Yazılım LTD.</h3>
								<span class="text-sm italic text-slate-500">ASBÜ Teknokent, Ankara</span>
								<span class="text-xs font-semibold text-resume-teal-dark bg-resume-teal/10 px-2 py-0.5 rounded">08/2021 - 10/2024</span>
							</div>
						</div>

						<div class="space-y-8 relative pl-4 border-l-2 border-slate-200 ml-0">
							<div class="relative">
								<div class="absolute -left-[23px] top-2 w-3 h-3 rounded-full bg-resume-teal border-3 border-white shadow-sm"></div>
								<div class="flex justify-between items-baseline mb-1">
									<h4 class="text-lg font-semibold text-slate-700">Remote Sensing & GIS Developer</h4>
								</div>

								<ul class="list-disc list-outside ml-4 space-y-2 text-sm text-slate-600 marker:text-resume-teal">
									<li>
										Performed satellite image analysis and GIS-based workflows using multispectral and hyperspectral remote sensing data. 
									</li>
									<li>
										Applied remote sensing techniques to agriculture-focused projects, including Land Use and Land Cover (LULC) classification and analysis. </li>
									<li>
										Worked with satellite platforms and geospatial tools such as Sentinel, Landsat, Google Earth Engine (GEE), ArcGIS Pro, and Copernicus datasets. </li>
									<li>
										Conducted satellite image preprocessing, analysis, and interpretation to support real-world GIS and environmental applications. 
									</li>
								</ul>
							</div>


							<div class="relative">
								<div class="absolute -left-[23px] top-2 w-3 h-3 rounded-full bg-resume-teal border-3 border-white shadow-sm"></div>
								<div class="flex justify-between items-baseline mb-1">
									<h4 class="text-lg font-semibold text-slate-700">NLP & Transformer Developer</h4>
								</div>

								<ul class="list-disc list-outside ml-4 mt-2 space-y-2 text-sm text-slate-600 marker:text-resume-teal">
									<li>Developed and applied Transformer-based architectures across NLP and computer vision domains.</li>
									<li>Applied Transformer architectures for text-based analysis, feature representation, and deep learning workflows.</li>
									<li>Worked on computer vision projects integrating Transformer-based approaches with modern deep learning pipelines.</li>
								</ul>
							</div>

							<div class="relative">
								<div class="absolute -left-[23px] top-2 w-3 h-3 rounded-full bg-resume-teal border-3 border-white shadow-sm"></div>
								<div class="flex justify-between items-baseline mb-1">
									<h3 class="text-lg font-semibold text-slate-700">Specialized Research & Advanced Systems</h3>
								</div>
								<ul class="list-disc list-outside ml-4 mt-2 space-y-2 text-sm text-slate-600 marker:text-resume-teal">
									<li>Trained deep learning models for fault detection using YOLO-based architectures in containerized (Docker) environments on remote Huawei Atlas 800 servers with NPU acceleration.</li>
									<li>Developed and managed server-side AI training workflows for computer vision-based infrastructure fault detection systems.</li>
									<li>Designed and implemented backend-oriented web applications using ASP.NET Core to support AI system management and data workflows.</li>
									<li>Conducted applied research in remote sensing for environmental and agriculture-related use cases.</li>
								</ul>
							</div>

							<div class="relative">
								<div class="absolute -left-[23px] top-2 w-3 h-3 rounded-full bg-resume-teal border-3 border-white shadow-sm"></div>
								<div class="flex justify-between items-baseline mb-1">
									<h4 class="text-lg font-semibold text-slate-700">AI & Computer Vision Engineer</h4>

								</div>
								<ul class="list-disc list-outside ml-4 space-y-2 text-sm text-slate-600 marker:text-resume-teal">
									<li>Developed AI-based image processing and computer vision applications using OpenCV and deep learning techniques, including OCR, image segmentation, object detection, and multi-object tracking.</li>
									<li>Built and curated datasets through data preparation, labeling, and augmentation to support supervised deep learning workflows.</li>
									<li>Applied GAN and Transformer-based image enhancement techniques, including super-resolution and feature representation learning.</li>
									<li>Designed and deployed real-time AI systems such as quality control pipelines, face recognition and identification platforms, and infrastructure inspection solutions, including high-voltage transmission line fault detection.</li>
									<li>Developed deep learning models using PyTorch and deployed inference services using Triton Inference Server and TorchServe.</li>
								</ul>
							</div>

							<div class="relative">
								<div class="absolute -left-[23px] top-2 w-3 h-3 rounded-full bg-resume-teal border-3 border-white shadow-sm"></div>
								<div class="flex justify-between items-baseline mb-1">
									<h3 class="text-lg font-semibold text-slate-700">MLOps & Backend Developer</h3>
								</div>
								<ul class="list-disc list-outside ml-4 mt-2 space-y-2 text-sm text-slate-600 marker:text-resume-teal">
									<li>Developed AI backend services using Python and FastAPI, with model inference served via NVIDIA Triton Inference Server.</li>
									<li>Designed and implemented microservice-based architectures to support scalable and modular software systems.</li>
									<li>Deployed and managed containerized applications using Docker, Kubernetes, and Proxmox for orchestration and virtualization.</li>
									<li>Built and maintained CI/CD pipelines using GitHub Actions, GHCR, ArgoCD, and Ansible to automate deployment workflows.</li>
									<li>Implemented real-time data processing pipelines using Kafka, Redis, and RTMP-based streaming systems.</li>
									<li>Monitored system performance and reliability using Prometheus and Grafana.</li>
								</ul>
							</div>
						</div>

						<div class="relative  mb-2">
							<div class="absolute -left-[25px] top-1 w-4 h-4 rounded-full bg-resume-gold border-4 border-white shadow-sm"></div>
							<div class="flex justify-between items-baseline mb-1">
								<h3 class="text-xl font-bold text-slate-900">Forensic Informatics Engineering Intern - I</h3>
								<span class="text-xs font-semibold text-resume-teal-dark bg-resume-teal/10 px-2 py-0.5 rounded">07/2021 - 08/2021</span>
							</div>

						</div>

						<div class="space-y-8 relative pl-4 border-l-2 border-slate-200 ml-0">
							<div class="relative">
								<div class="absolute -left-[23px] top-2 w-3 h-3 rounded-full bg-resume-teal border-3 border-white shadow-sm"></div>
								<div class="flex justify-between items-baseline mb-3">
									<h4 class="text-lg font-semibold text-slate-700">DGH Arge Yazılım</h4>
									<span class="text-sm italic text-slate-500">İnönü Teknokent, Malatya</span>
								</div>
								<div>
									<ul class="list-disc list-outside ml-4 space-y-2 text-sm text-slate-600 marker:text-resume-teal">
										<li>Gained foundational experience in AI-based software development using Python, PyTorch, TensorFlow, and OpenCV.</li>
										<li>Assisted in developing and integrating backend services using ASP.NET Core and frontend components with Vue.js</li>
										<li>Explored real-time communication mechanisms using Socket.IO for web-based applications.</li>
										<li>Supported real-time web integration tasks as part of short-term internship projects.</li>
									</ul>
								</div>
							</div>
						</div>

						<div class="relative  mb-2">
							<div class="absolute -left-[25px] top-1 w-4 h-4 rounded-full bg-resume-gold border-4 border-white shadow-sm"></div>
							<div class="flex justify-between items-baseline mb-1">
								<h3 class="text-xl font-bold text-slate-900">Forensic Informatics Engineering Intern - II</h3>
								<span class="text-xs font-semibold text-resume-teal-dark bg-resume-teal/10 px-2 py-0.5 rounded">07/2020 - 08/2020</span>
							</div>
						</div>

						<div class="space-y-8 relative pl-4 border-l-2 border-slate-200 ml-0">
							<div class="relative">
								<div class="absolute -left-[23px] top-2 w-3 h-3 rounded-full bg-resume-teal border-3 border-white shadow-sm"></div>
								<div class="flex justify-between items-baseline mb-3">
									<h4 class="text-lg font-semibold text-slate-700">Telehouse İstanbul - Teknotel</h4>
									<span class="text-sm italic text-slate-500">Kozyatağı, Kadıköy/İstanbul</span>
								</div>
								<div>
									<ul class="list-disc list-outside ml-4 space-y-2 text-sm text-slate-600 marker:text-resume-teal">
										<li>Worked on basic network infrastructure tasks, including routing and protocol configurations using Cisco Packet Tracer.</li>
										<li>Assisted in server setup and configuration on HP enterprise servers, including SAS disk configuration and RAID setup.</li>
										<li>Gained practical experience with REST-based architectures and web hosting systems, including WHMCS-based service management workflows.</li>
										<li>Supported network and IT operations related to server, storage, and connectivity management.</li>
									</ul>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<!-- Projects -->
			<section>
				<h2 class="text-resume-gold text-2xl font-bold uppercase border-b-2 border-resume-gold/30 pb-2 mb-2 tracking-wide">Projects</h2>
				<div class="space-y-8 relative pl-4 border-l-2 border-slate-200 ml-2">
					<div class="relative">
						<div class="absolute -left-[25px] top-1 w-4 h-4 rounded-full bg-resume-teal border-4 border-white shadow-sm"></div>
						<h3 class="text-lg font-bold text-slate-900 mb-0 mt-1">LULC Analysis & Remote Sensing</h3>
						<ul class="list-disc list-outside ml-4 text-md text-slate-600 marker:text-resume-teal">
							<li>
								Performed land use and land cover (LULC) segmentation using multispectral Sentinel-2 and Landsat satellite imagery.
							</li>
							<li>
								Generated composite datasets and conducted geospatial analysis to calculate area distribution of land cover classes using ArcGIS Pro and QGIS.
							</li>
							<li>
								Built scalable data collection and processing pipelines for LULC analysis using Google Earth Engine (GEE) APIs and Copernicus datasets.
							</li>
							<li>
								Retrieved and processed map tiles via web-based geospatial protocols to support large-scale spatial analysis workflows.
							</li>
							<li>
								Produced digital elevation models (DEM) from radar data using InSAR-based techniques for terrain and elevation analysis.
							</li>
						</ul>
					</div>

					<div class="relative">
						<div class="absolute -left-[25px] top-1 w-4 h-4 rounded-full bg-resume-teal border-4 border-white shadow-sm"></div>
						<h3 class="text-lg font-bold text-slate-900 mb-0 mt-1">Fault Detection Project - High-Voltage Power Lines</h3>
						<ul class="list-disc list-outside ml-4 text-md text-slate-600 marker:text-resume-teal">
							<li>
								Developed a computer vision–based fault detection system for high-voltage transmission lines using YOLOv11-based object detection and segmentation models.
							</li>
							<li>
								Designed and implemented detection and segmentation pipelines for high-voltage line components, including insulators, poles, and structural elements.
							</li>
							<li>
								Built object detection models for identifying bird nests on transmission lines to support proactive maintenance and infrastructure safety.
							</li>
							<li>Prepared and annotated custom datasets to train and evaluate detection and segmentation models.</li>
						</ul>
					</div>

					<div class="relative">
						<div class="absolute -left-[25px] top-1 w-4 h-4 rounded-full bg-resume-teal border-4 border-white shadow-sm"></div>

						<h3 class="text-lg font-bold text-slate-900 mb-0 mt-1">
							Multi-Camera AI-Based Security & Surveillance System
						</h3>

						<ul class="list-disc list-outside ml-4 text-md text-slate-600 marker:text-resume-teal">
							<li>
								Developed an integrated computer vision system combining face detection, recognition, multi-object tracking, and person re-identification for identity-based surveillance applications.
							</li>
							<li>
								Integrated additional AI modules including mask detection, emotion recognition, and weapon detection to enhance security monitoring capabilities.
							</li>
							<li>
								Designed the system to operate across multiple camera streams with real-time video processing and event-based analytics.
							</li>
							<li>
								Architected a microservice-based backend and deployed AI inference services using Docker and Kubernetes for scalable and fault-tolerant operation.
							</li>
							<li>
								Built gRPC backend APIs to manage video streams, inference services, and coordination across distributed system components.
							</li>
						</ul>
					</div>

					<div class="relative">
						<div class="absolute -left-[25px] top-1 w-4 h-4 rounded-full bg-resume-teal border-4 border-white shadow-sm"></div>

						<h3 class="text-lg font-bold text-slate-900 mb-0 mt-1">
							Real-Time Tennis Performance Analysis System
						</h3>
						<ul class="list-disc list-outside ml-4 text-md text-slate-600 marker:text-resume-teal">
							<li>
								Developed a real-time computer vision system for tennis ball detection, tracking, and bounce point estimation to support ITN (International Tennis Number) performance evaluation.
							</li>
							<li>
								Implemented player detection and tracking pipelines to analyze shot events and score player actions during live gameplay.
							</li>
							<li>
								Designed multi-camera calibration, alignment, and synchronization mechanisms to fuse ball trajectories across multiple camera viewpoints.
							</li>
							<li>
								Applied human pose estimation techniques to analyze player movements and assess performance progression based on body posture and motion patterns.
							</li>
							<li>
								Built low-latency processing workflows to enable real-time feedback in sports performance analysis systems.
							</li>
						</ul>
					</div>

					<div class="relative">
						<div class="absolute -left-[25px] top-1 w-4 h-4 rounded-full bg-resume-teal border-4 border-white shadow-sm"></div>

						<h3 class="text-lg font-bold text-slate-900 mb-0 mt-1">Securing Incident Reporting Processes with Blockchain</h3>
						<ul class="list-disc list-outside ml-4 text-md text-slate-600 marker:text-resume-teal">
							<li>Designed and implemented a blockchain-based system to ensure immutability and consistency of digital forensic data processing and reporting pipelines.</li>
							<li>
								Applied blockchain mechanisms to preserve evidence integrity and prevent tampering during forensic analysis workflows.
							</li>
							<li>
								Developed system components using PHP and Java to support secure data handling and auditability.
							</li>
							<li>
								Implemented hash-based (SHA256) verification to validate forensic data consistency across the pipeline.
							</li>
						</ul>
					</div>
				</div>
			</section>
		</div>

		<!-- Column 1 -->
		<div class="md:col-span-5 lg:col-span-4 space-y-10">
			<section>
				<h2 class="text-resume-gold text-2xl font-bold uppercase border-b-2 border-resume-gold/30 pb-2 mb-2 tracking-wide">
					Technical Skills
				</h2>

				<div class="space-y-4">
					<div *ngFor="let group of technicalSkillGroups">
						<h3 class="text-resume-teal font-semibold mb-1 text-xs uppercase tracking-wide">
							{{ group.title }}
						</h3>

						<div class="flex flex-wrap gap-2">
							<span *ngFor="let skill of group.items" class="px-2.5 py-1 bg-slate-100 text-slate-700 text-[11px] font-bold rounded-sm border border-slate-200 print:bg-none print:border-none print:px-0 print:text-sm print:inline print:after:content-[',_'] print:last:after:content-['']">
								{{ skill }}
							</span>
						</div>
					</div>
				</div>
			</section>

			<section>
				<h2 class="text-resume-gold text-2xl font-bold uppercase border-b-2 border-resume-gold/30 pb-2 mb-2 tracking-wide">Language</h2>
				<div class="space-y-4">
					<div>
						<h3 class="text-base font-bold text-slate-800 leading-tight mb-1">English - Professional Working Proficiency</h3>
					</div>
					<div>
						<h3 class="text-base font-bold text-slate-800 leading-tight mb-1">Turkish - Native</h3>
					</div>
				</div>
			</section>

			<section>
				<h2 class="text-resume-gold text-2xl font-bold uppercase border-b-2 border-resume-gold/30 pb-2 mb-2 tracking-wide">Education</h2>
				<div>
					<h3 class="text-lg font-bold text-slate-900 leading-tight">Fırat University</h3>
					<h4 class="text-base font-medium text-slate-700 mb-1">Bachelor of Science in Forensic Informatics Engineering</h4>
					<p class="text-md text-resume-gold font-medium"><strong>GNO:</strong> 3.73 / 4</p>
					<p class="text-sm text-resume-teal font-medium italic">2017 - 2022</p>
				</div>
			</section>
		</div>
	</main>
</div>